/**
 * Power Distribution edit body for the descriptor engine (#997 Story 6.7, mirror
 * of `LargeUpsEngineEditFormBody`).
 *
 * Winston's ADR (parity by identity): the engine renders the EXISTING bespoke
 * `PowerDistributionEditFormContent` — the user-approved Power Distribution editor
 * (field set, order, labels, placeholders, `FormTwoOptionChoice` type toggle, the
 * numeric busway-length control, BOTH per-unit attachment carousels — details
 * `FACILITY_POWER_DISTRIBUTION` + additional-info
 * `FACILITY_POWER_DISTRIBUTION_ADDITIONAL`, and the Additional-Info card) — bound to
 * the engine's edit-session state. The engine owns list/save/flush/attachment
 * lifecycle (the 6.1/6.2 fixes), the section owns its view.
 *
 * `PowerDistributionEditFormContent` gates its Additional-Info card behind
 * `showAdditionalInformation`, so the host threads it (computed via
 * `shouldShowFacilityPowerAdditionalInformationInUnitEditor`) + `showFullDetails`
 * through the `editBody` closure — same shape as `LargeUpsEngineEditFormBody`.
 *
 * Binding contract:
 * - `formData` is PD-shaped: the engine's editor field map is coerced back through
 *   `descriptorFieldMapToPatch`, plus identity (`name` ← `instanceName`, `status` ←
 *   `instanceStatus`) and the unit id (`id` ← `configurationId`, which
 *   `PowerDistributionEditFormContent` uses to scope its two per-unit attachment
 *   cards — 6.1).
 * - `updateField` routes identity keys to the engine identity setters and every
 *   data field to `setInstanceField`, converting bespoke values to the engine's
 *   editor forms (undefined → '', boolean kept). The busway-length numeric control
 *   writes a number that is stringified for the engine session; the descriptor
 *   types the field `text` (stored NVARCHAR), so no number coercion false-dirties
 *   untouched units.
 */

import React, { useCallback, useMemo } from 'react';

import { PowerDistributionEditFormContent } from '../../../../components/powerdistribution/PowerDistributionEditFormContent';
import { normalizePowerDistributionStatus } from '../../../../components/powerdistribution/powerDistributionListUtils';
import { descriptorFieldMapToPatch } from '../../../sections/descriptorEngineAdapter';
import { POWER_DISTRIBUTION_DESCRIPTOR } from '../../../sections/powerDistribution.descriptor';

import type { RepeatableInstanceEditFormRenderProps } from '../../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { PowerDistributionConfiguration } from '../../../../types/powerDistribution';

export type PowerDistributionEngineEditFormBodyProps =
  RepeatableInstanceEditFormRenderProps & {
    /** Power Distribution gates its Additional-Info card (mirror Large UPS). */
    showAdditionalInformation?: boolean;
    showFullDetails?: boolean;
  };

export function PowerDistributionEngineEditFormBody({
  instanceFields,
  setInstanceField,
  instanceName,
  instanceStatus,
  setInstanceName,
  setInstanceStatus,
  disabled,
  requestStage,
  requestId,
  configurationId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  offlineMode,
  onCaptionChange,
  pendingCaptionEdits,
  markDirty,
  showAdditionalInformation,
  showFullDetails,
}: PowerDistributionEngineEditFormBodyProps): React.ReactElement {
  const toPatch = useMemo(
    () => descriptorFieldMapToPatch(POWER_DISTRIBUTION_DESCRIPTOR),
    [],
  );

  const formData = useMemo((): Partial<PowerDistributionConfiguration> => {
    const {
      instanceName: patchName,
      instanceStatus: patchStatus,
      ...dataFields
    } = toPatch(instanceFields);
    return {
      ...(dataFields as Partial<PowerDistributionConfiguration>),
      id: configurationId,
      name: instanceName ?? (patchName as string | undefined),
      status: normalizePowerDistributionStatus(
        instanceStatus ?? (patchStatus as string | undefined),
      ),
    };
  }, [toPatch, instanceFields, configurationId, instanceName, instanceStatus]);

  const updateField = useCallback(
    <K extends keyof PowerDistributionConfiguration>(
      field: K,
      value: PowerDistributionConfiguration[K],
    ) => {
      if (field === 'name') {
        const name = typeof value === 'string' ? value : '';
        if (setInstanceName) {
          setInstanceName(name);
        } else {
          setInstanceField('instanceName', name);
        }
        return;
      }
      if (field === 'status') {
        const status = normalizePowerDistributionStatus(
          typeof value === 'string' ? value : undefined,
        );
        if (setInstanceStatus) {
          setInstanceStatus(status);
        } else {
          setInstanceField('instanceStatus', status);
        }
        return;
      }
      // The engine session stores editor forms: booleans stay booleans (yesNo),
      // a cleared value becomes blank — never 'undefined'. The busway-length
      // number control writes a number; stringified here (descriptor type=text).
      const editorValue =
        typeof value === 'boolean' ? value : value == null ? '' : String(value);
      setInstanceField(field as string, editorValue);
    },
    [setInstanceField, setInstanceName, setInstanceStatus],
  );

  const onFilesSelectedDirty = useMemo(
    () =>
      onFilesSelected
        ? (sectionId: string, files: Parameters<typeof onFilesSelected>[1]) => {
            markDirty();
            onFilesSelected(sectionId, files);
          }
        : undefined,
    [markDirty, onFilesSelected],
  );

  return (
    <PowerDistributionEditFormContent
      formData={formData}
      updateField={updateField}
      disabled={disabled}
      requestStage={requestStage}
      requestId={requestId}
      attachmentsBySection={attachmentsBySection}
      onAttachFiles={onAttachFiles}
      onDeleteAttachment={onDeleteAttachment}
      selectedFiles={selectedFiles}
      onFilesSelected={onFilesSelectedDirty}
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
      showAdditionalInformation={showAdditionalInformation}
      showFullDetails={showFullDetails}
    />
  );
}
